import { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getBaseUrl } from '../config/appConfig';

const AuthContext = createContext(null);

// Get backend URL dynamically
const getBackendUrl = () => getBaseUrl();

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('session_token');
  const headers = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

// Helper for authenticated fetch requests
const authFetch = async (url, options = {}) => {
  const token = localStorage.getItem('session_token');
  const headers = {
    ...options.headers,
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return fetch(url, {
    ...options,
    headers,
    credentials: 'include',
  });
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [company, setCompany] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [apiKeyWarnings, setApiKeyWarnings] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Check authentication on mount
  useEffect(() => {
    // Skip if we have user from location state (just logged in)
    if (location.state?.user) {
      setUser(location.state.user);
      // Store token if available
      if (location.state?.token) {
        localStorage.setItem('session_token', location.state.token);
      }
      setLoading(false);
      // Clear location state
      window.history.replaceState({}, document.title);
      // Fetch company data
      fetchCompanyData();
      return;
    }

    checkAuth();
  }, []);
const checkAuth = async () => {
    try {
      const response = await authFetch(`${getBackendUrl()}/api/auth/me`);

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        await fetchCompanyData();
      } else {
        // Clear invalid token
        localStorage.removeItem('session_token');
        setUser(null);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanyData = async () => {
    try {
      const response = await authFetch(`${getBackendUrl()}/api/subscriptions/company`);

      if (response.ok) {
        const data = await response.json();
        setCompany(data.company);
        setSubscription(data.subscription);
        setApiKeyWarnings(data.api_key_warnings);
      }
    } catch (error) {
      console.error('Failed to fetch company data:', error);
    }
  };

  const logout = async () => {
    try {
      await authFetch(`${getBackendUrl()}/api/auth/logout`, {
        method: 'POST',
      });
    } catch (error) {
      console.error('Logout error:', error);
    }

    // Clear token from localStorage
    localStorage.removeItem('session_token');
    setUser(null);
    setCompany(null);
    setSubscription(null);
    navigate('/');
  };

  const login = () => {
    // Redirect to backend Google OAuth endpoint
    const backendUrl = getBackendUrl();
    if (!backendUrl) {
      console.error('Backend URL is not configured');
      return;
    }
    window.location.href = `${backendUrl}/api/auth/google`;
  };

  // Function to store token after login
  const setAuthToken = (token) => {
    if (token) {
      localStorage.setItem('session_token', token);
    }
  };

  const value = {
    user,
    company,
    subscription,
    apiKeyWarnings,
    loading,
    isAuthenticated: !!user,
    isSuperAdmin: user?.role === 'super_admin' || user?.is_super_admin,
    isAdmin: user?.role === 'admin' || user?.role === 'super_admin' || user?.is_super_admin,
    // Show admin panel link ONLY for users with explicit backoffice access
    // Regular company admins (role === 'admin') do NOT get backoffice access
    // Must have explicit is_backoffice_user or is_super_admin flag
    hasAdminAccess: !user?.access_revoked && (
                    user?.is_super_admin === true || 
                    user?.is_backoffice_user === true ||
                    user?.is_internal_user === true),
    login,
    logout,
    checkAuth,
    fetchCompanyData,
    setAuthToken,
    authFetch,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Export helper for use outside React components
export { authFetch, getAuthHeaders };
